import { useState } from 'react';
import { Check, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/services/queryKeyFactory';
import { useAuth } from '@/contexts/AuthContext';
import Loader from '@/components/Loader';
import { PLAN_ICON_MAPPER } from '@/constants';
import { IN_APP_ROUTES } from '@/constants/urls';
import { useNavigate } from 'react-router-dom';
import razorPayCheckoutHandler from '@/helpers/razorPayCheckoutHandler';
import { usePlans } from '@/contexts/PlanRulesContext';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';
import { useCompset } from '@/contexts/CompsetContext';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PlanDetailsCard from '@/components/cards/PlanDetailsCard';
import { Badge } from '@/components/ui/badge';


const PlanSelectionPage = () => {

    const { isAuthenticated } = useAuth();

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [planMode, setPlanMode] = useState("subscription");

    const handleSelectPlan = async (planId) => {
        setSelectedPlan(planId);
    };

    const { plans, isLoadingPlans, isErrorPlans } = usePlans()
    const { activeCompset } = useCompset()

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const onSuccessfullPayment = async () => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.me() })
        navigate(IN_APP_ROUTES.COMPSET_PAGE)
        // window.location.pathname = IN_APP_ROUTES.COMPSET_PAGE
    }

    const paymentHandler = async () => {

        try {

            if (!selectedPlan) {
                toast.error("Select a plan to continue")
                return
            }

            setLoading(true)

            const plan = plans?.plans?.find(plan => plan?.id == selectedPlan)

            if (plan?.type === "regular") {

                const paymentStatus = await razorPayCheckoutHandler(
                    plan?.amount,
                    plan?.currency,
                    plan?.id,
                    plan?.name,
                    plan?.type
                )

                if (paymentStatus) {
                    await onSuccessfullPayment()
                }

                return

            } else {

                const paymentStatus = await razorPayCheckoutHandler(
                    plan?.amount,
                    plan?.currency,
                    plan?.id,
                    plan?.name,
                )


                if (paymentStatus) {
                    await onSuccessfullPayment()
                }

            }

        } catch (error) {
            apiErrorHandler(error)
        } finally {
            setLoading(false)
        }

    }

    if (isLoadingPlans) {
        return <Loader />
    }

    const subscriptionPlans = plans?.plans?.filter(plan => plan?.type === "subscription")
    const regularPlans = plans?.plans?.filter(plan => plan?.type === "regular")

    // planMode === "subscription"
    //     ? plans?.plans?.filter(plan => plan?.type === "subscription")
    //     : plans?.plans?.filter(plan => plan?.type === "regular")

    return (
        <div className="space-y-6 py-4" data-testid="plan-selection-page">

            {/* Header */}
            <div className="text-center max-w-2xl mx-auto">
                <h1 className="text-2xl font-bold mb-2">Plan Selection</h1>
                <p className="text-muted-foreground">
                    Choose the plan that best fits your hotel's needs. You can always upgrade or adjust later as your business grows.
                </p>
            </div>

            {/* Tabs */}
            <div className='flex justify-center'>
                {/* <Tabs value={planMode} onValueChange={setPlanMode}>
                    <TabsList className="h-8">
                        <TabsTrigger value="subscription" className="text-xs px-2">
                            Subscription
                        </TabsTrigger>
                        <TabsTrigger value="regular" className="text-xs px-2">
                            Regular
                        </TabsTrigger>
                    </TabsList>
                </Tabs> */}
            </div>

            <div>
                <div>
                    <div className="flex items-center justify-center gap-3 mb-6">
                        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 px-4 py-1">
                            Pay As You Go
                        </Badge>
                        <span className="text-sm text-muted-foreground">Perfect for trying out</span>
                    </div>
                    {/* Plans */}
                    <div className="flex flex-wrap justify-center gap-6 max-w-5xl mx-auto">

                        {regularPlans?.map((plan) => {

                            return (

                                <PlanDetailsCard
                                    plan={plan}
                                    planMode={planMode}
                                    selectedPlan={selectedPlan}
                                    onPlanSelect={handleSelectPlan}
                                />
                            )
                        })}
                    </div>
                </div>
                <div className='mt-9'>
                    <div className="flex items-center gap-4 max-w-3xl mx-auto mb-12">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-sm text-muted-foreground font-medium px-4">OR SUBSCRIBE MONTHLY</span>
                        <div className="flex-1 h-px bg-border" />
                    </div>

                    <div className="flex flex-wrap justify-center gap-6 max-w-5xl mx-auto">

                        {subscriptionPlans?.map((plan) => {

                            return (

                                <PlanDetailsCard
                                    plan={plan}
                                    planMode={planMode}
                                    selectedPlan={selectedPlan}
                                    onPlanSelect={handleSelectPlan}
                                />
                            )
                        })}
                    </div>
                </div>


            </div>

            {/* Continue Button */}
            <div className="flex justify-center">

                <Button
                    size="lg"
                    onClick={paymentHandler}
                    disabled={loading}
                    className="min-w-[200px]"
                >

                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                        </>
                    ) : (
                        'Continue'
                    )}

                </Button>

            </div>

            {/* Terms */}
            <p className="text-center text-xs text-muted-foreground max-w-md mx-auto">
                By selecting a plan, you agree to our Terms of Service and Privacy Policy.
                Paid plans will be charged monthly. Cancel anytime.
            </p>

        </div>
    );
};

export default PlanSelectionPage;